/**
 * Profile validation utilities
 * Used to determine if a user has a valid, complete profile
 */

import { customGenderOptions } from "./customGenderOptions";

// Valid gender values that the system accepts
const VALID_GENDERS = ["Men", "Women", ...customGenderOptions];

/**
 * Validate date of birth
 * Must be a valid date, not in the future, and represent a reasonable age (16-120 years)
 * Users must be 16+ per our Terms of Service
 */
export const isValidDateOfBirth = (dob) => {
  if (!dob || typeof dob !== "string") return false;

  const parsed = new Date(dob);

  // Must be a valid parseable date
  if (isNaN(parsed.getTime())) return false;

  const now = new Date();

  // Must not be in the future
  if (parsed > now) return false;

  // Calculate age
  const ageDiff = now.getFullYear() - parsed.getFullYear();
  const monthDiff = now.getMonth() - parsed.getMonth();
  const dayDiff = now.getDate() - parsed.getDate();

  let age = ageDiff;
  if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) {
    age--;
  }

  // Must be at least 16 years old (per Terms of Service) and no more than 120 years old
  // This catches garbage data like "1640" or future dates
  return age >= 16 && age <= 120;
};

/**
 * Validate gender value
 * Must be one of the accepted gender options
 */
export const isValidGender = (gender) => {
  if (!gender || typeof gender !== "string") return false;
  return VALID_GENDERS.includes(gender);
};

/**
 * Validate avatar URL
 * Must be a non-empty string starting with data:image or http
 */
export const isValidAvatarURL = (url) => {
  if (!url || typeof url !== "string") return false;
  return url.startsWith("data:image") || url.startsWith("http");
};

/**
 * Check if a user profile is complete and valid
 * Used to determine if user needs to go through registration
 */
export const isProfileComplete = (user) => {
  if (!user) return false;

  const hasValidGender = isValidGender(user.gender);
  const hasValidDob = isValidDateOfBirth(user.date_of_birth);
  const hasValidAvatar = isValidAvatarURL(user.avatar);

  return hasValidGender && hasValidDob && hasValidAvatar;
};
